import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import useAxiosInstance from "../../../services/useAxiosInstance";
import { useSelector } from "react-redux";

const ServiceRequestChart = ({ startDate, endDate }) => {
    const axiosInstance = useAxiosInstance();
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const roleId = useSelector((state) => state.auth.roleId);
    const clientId = useSelector((state) => state.auth.clientId);
    const communityIds = useSelector((state) => state.auth.communityId);

    // Role-based parameter construction
    const getApiParameters = () => {
        let communityIdParam, clientIdParam;

        switch (roleId) {
            case 1000: // Superadmin - All communities
                communityIdParam = 0;
                clientIdParam = 0;
                break;
            case 1001: // HOA Admin - Specific community, all clients
            case 1002: // Board Member - Specific community, all clients
                communityIdParam = communityIds;
                clientIdParam = 0;
                break;
            case 1003: // User - Specific community and client
                communityIdParam = communityIds;
                clientIdParam = clientId;
                break;
            default:
                communityIdParam = communityIds;
                clientIdParam = clientId;
        }

        return { communityIdParam, clientIdParam };
    };

    const fetchServiceRequestData = async () => {
        if (!startDate || !endDate) {
            console.warn("Start date or end date is not defined");
            setChartData(getEmptyData());
            return;
        }

        console.log("Fetching data for dates:", startDate, endDate);
        setLoading(true);
        setError(null);

        const { communityIdParam, clientIdParam } = getApiParameters();

        try {
            const response = await axiosInstance.get(
                `/api/public/servicerequest/admin/getAllServiceRequests`,
                {
                    params: {
                        communityId: communityIdParam,
                        requestedClientId: clientIdParam,
                        endDate: endDate,
                        startDate: startDate,
                    },
                }
            );

            console.log("Service request chart data received:", response.data);

            if (response) {
                const formattedData = [
                    {
                        name: "Pending",
                        value: response.data.pending || 0,
                        fill: "#95A4FC",
                    },
                    {
                        name: "Work Done",
                        value: response.data.workDone || 0,
                        fill: "#FF6B6B",
                    },
                    {
                        name: "Vendor Assigned",
                        value: response.data.vendorAssigned || 0,
                        fill: "#FFD93D",
                    },
                    {
                        name: "Canceled",
                        value: response.data.canceled || 0,
                        fill: "#4ECDC4",
                    },
                ];
                setChartData(formattedData);
            } else {
                console.warn("Received empty data");
                setChartData(getEmptyData());
            }
        } catch (error) {
            console.error("Error fetching service request data:", error);
            setError("Failed to load service request data");
            setChartData(getEmptyData());
        } finally {
            setLoading(false);
        }
    };

    // Helper function for empty data
    const getEmptyData = () => [
        { name: "Pending", value: 0, fill: "#95A4FC" },
        { name: "Work Done", value: 0, fill: "#FF6B6B" },
        { name: "Vendor Assigned", value: 0, fill: "#FFD93D" },
        { name: "Canceled", value: 0, fill: "#4ECDC4" },
    ];

    useEffect(() => {
        fetchServiceRequestData();
    }, [startDate, endDate, roleId, communityIds, clientId]);

    // Role-based title
    const getChartTitle = () => {
        switch (roleId) {
            case 1000:
                return "Service Requests - All Communities";
            case 1001:
            case 1002:
                return "Service Requests - Community Overview";
            case 1003:
                return "My Service Requests";
            default:
                return "Service Requests";
        }
    };

    return (
        <div>
            <div className="p-5">
                <div>
                    <h3 className="text-lg font-semibold text-gray-700">{getChartTitle()}</h3>
                    {loading && <p className="text-sm text-gray-500">Loading data...</p>}
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    {!loading && !error && (
                        <p className="text-sm text-gray-500">
                            {roleId === 1000 ? "System-wide service request overview" :
                                roleId === 1001 || roleId === 1002 ? "Community service request status" :
                                    roleId === 1003 ? "Your personal service request history" :
                                        "Service request overview"}
                        </p>
                    )}
                </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    width={800}
                    height={400}
                    data={chartData}
                    barSize={40}
                    margin={{ top: 20, right: 40, left: 40, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0} />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                        interval={0}
                        angle={0}
                        textAnchor="middle"  // This centers the labels under the bars
                        height={80}
                    />
                    <YAxis
                        tickFormatter={(value) => `${value}`}
                        ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        width={10}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                        formatter={(value, name) => [value, name]}
                        labelStyle={{ color: '#374151' }}
                        contentStyle={{
                            backgroundColor: '#f9fafb',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px'
                        }}
                    />
                    <Bar
                        dataKey="value"
                        fill={(entry) => entry.fill}
                        radius={[5, 5, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>

            {/* Add custom styles for hiding the legend */}
            <style jsx global>{`
        .recharts-default-legend {
          display: none;
        }
      `}</style>
        </div>
    );
};

export default ServiceRequestChart;